import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    return {
      isConnected: false,
      onlineUsers: 0,
      lastActivity: null,
      leadScoreUpdates: [],
      sendMessage: () => {},
    };
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [lastActivity, setLastActivity] = useState(null);
  const [leadScoreUpdates, setLeadScoreUpdates] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const connectRef = useRef(null);

  // Define handleNotification first so it can be used in connect
  const handleNotification = useCallback((notification) => {
    if (!notification) return;
    
    const { type, title, message, priority } = notification;
    
    // Show toast based on priority
    if (priority === 'high') {
      toast.error(title, {
        description: message,
        duration: 10000,
        action: {
          label: 'View',
          onClick: () => {
            // Navigate to relevant page
            if (notification.action_url) {
              window.location.href = notification.action_url;
            }
          }
        }
      });
    } else if (type === 'approval_required') {
      toast.warning(title, {
        description: message,
        duration: 8000,
      });
    } else {
      toast.info(title, {
        description: message,
        duration: 5000,
      });
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || !user) return;
    
    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullWsUrl = `${wsUrl}/ws/${token}`;
    
    try {
      wsRef.current = new WebSocket(fullWsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 25000);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connected':
              setOnlineUsers(data.online_users || 1);
              break;
              
            case 'notification':
            case 'team_notification':
              handleNotification(data.data);
              // Check for branding update
              if (data.data?.type === 'branding_update') {
                // Dispatch custom event for BrandingContext
                window.dispatchEvent(new CustomEvent('ws_branding_update', {
                  detail: data.data
                }));
              }
              break;
              
            case 'activity_update':
              setLastActivity(data.data);
              break;
              
            case 'lead_score_update':
              setLeadScoreUpdates(prev => [...prev.slice(-10), data.data]);
              break;
              
            case 'keepalive':
              // Just acknowledge
              break;
              
            default:
              console.log('Unknown WS message type:', data.type);
          }
        } catch (e) {
          // Ignore non-JSON messages (like pong)
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconnect after 5 seconds if not intentional close
        if (event.code !== 1000 && user) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting WebSocket reconnect...');
            connectRef.current?.();
          }, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [token, user, handleNotification]);

  // Store connect in ref for self-reference
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    };
  }, [connect]);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      onlineUsers,
      lastActivity,
      leadScoreUpdates,
      sendMessage,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};
